import React, { useEffect, useState, useCallback } from 'react';
import '../../componentsStyling/ContentStyling.css';
import Snackbar from '@mui/material/Snackbar'; // Import Snackbar for success/failure messages
import CircularProgress from '@mui/material/CircularProgress';
import { useReservationApis } from '../../apis/ReservationAPIs.js';
// import { CreateReservationLog } from './CreateReservationLog.js'
import CreateReservationDialog from './Crud/AddReservation.js';
import { getDetailedCheckOutColumns, getDetailedCheckInColumns } from './Tables/reservationDetailedDatagrids.js';
import { checkOutColumns, checkInColumns } from './Tables/reservationDatagrids.js';
import useSelectedDate from '../general/getCurrentDate.js';
import ItemDetailsDialog from './Dialogs/ItemDetailsDialog.js';
import ReservationHeader from './ReservationHeader.js';
import { ReservationBundleDialog } from './Dialogs/ReservationBundleDialog.js';
import { ReservationsTable } from './Tables/ReservationsTable.js';
import useWindowSize from '../../utils/windowSize.js';

function Reservations({ userRole, userId }) {
    const { fetchReservationBundles, cancelReservation, updateReservation } = useReservationApis();

    const [checkOut, setCheckOut] = useState([]);
    const [checkIn, setCheckIn] = useState([]);
     // Snackbar state
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    // for row selection
    const [itemDetailsDialogOpen, setItemDetailsDialogOpen] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [dialogType, setDialogType] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const [reservationBundleDialogOpen, setReservationBundleDialogOpen] = useState(false);
    const [selectedBundleRowData, setSelectedBundleRowData] = useState(null);
    
    // Date Selection, Defaults to current date
    const [selectedDate, setSelectedDate] = useSelectedDate();
    
    const handleDateChange = (event) => {
        setSelectedDate(event.target.value); // This will automatically update the state
        };

    const handleMainTableRowClick = (params, type) => {
        setDialogType(type);
        setSelectedBundleRowData(params.row);
        console.log(params.row)
        setReservationBundleDialogOpen(true);
    };

    const reservationBundleDialogClose = () => {
        setReservationBundleDialogOpen(false);
        setSelectedBundleRowData(null);
        setDialogType(null);
        // Clear selections when dialog closes
        // setSelectedCheckouts([]); // Removed as per edit hint
        // setSelectedCheckins([]); // Removed as per edit hint
    };

      
    // Search
    const [checkOutSearch, setCheckOutSearch] = useState("");
    const [checkInSearch, setCheckInSearch] = useState("");

    const fetchReservationsData = useCallback(async () => {
        setLoading(true);
        try {
            const { checkOutData, checkInData } = await fetchReservationBundles(selectedDate);
            // Ensure we always have arrays, even if the API returns something unexpected
            setCheckOut(Array.isArray(checkOutData) ? checkOutData : []);
            setCheckIn(Array.isArray(checkInData) ? checkInData : []);
        } catch (error) {
            console.error("Failed to fetch reservations:", error);
            // Set empty arrays on error to prevent runtime errors
            setCheckOut([]);
            setCheckIn([]);
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);


    useEffect(() => {
        const loadReservations = () => {
            fetchReservationsData();
        };
        loadReservations();
    }, [fetchReservationsData]);


    const handleCancelReservation = async (event, reservationId) => {
        const ok = await cancelReservation(reservationId);
        if (!ok) {
            return alert("Failed to cancel reservation.");
        }

        // Show success snackbar
        setSnackbarMessage("Reservation canceled successfully");
        setOpenSnackbar(true);

        // Refresh top-level tables
        await fetchReservationsData();

        // If we have a selected bundle open, remove the reservation from it:
        setSelectedBundleRowData(prev => {
            if (!prev) return prev;

            // compute the new reservations array
            const updatedReservations = prev.reservations.filter(r => r.reservationId !== reservationId);

            // if none left, close the dialog
            if (updatedReservations.length === 0) {
                reservationBundleDialogClose();
            return null; // clear out the selected data entirely
            }

            // otherwise return the updated bundle
            return {
            ...prev,
            reservations: updatedReservations
            };
        });

        // Also update the master list so if the dialog was still referencing it, it stays in sync
        if (dialogType === "checkOut") {
            setCheckOut(prevBundles =>
            prevBundles.map(bundle => {
                if (bundle.bundleId !== selectedBundleRowData.bundleId) return bundle;
                const updated = bundle.reservations.filter(r => r.reservationId !== reservationId);
                return { ...bundle, reservations: updated };
            })
            );
        } 
    };

    // Wrapper function to handle the parameter order for the column functions
    const handleCancelWrapper = (event, reservationId) => {
        handleCancelReservation(event, reservationId);
    };

    // Handle individual checkout/checkin actions
    const handleReservationCall = async (updateData, reservationId, actionType) => {
        try {
            const success = await updateReservation(updateData, reservationId);
            if (!success) {
                return alert('Failed to update reservation. Please try again.');
            }

            // Show success snackbar
            const actionText = actionType === 'checkOut' ? 'checked out' : 'checked in';
            setSnackbarMessage(`Reservation ${actionText} successfully`);
            setOpenSnackbar(true);

            // Refresh top-level tables
            await fetchReservationsData();

            // If we have a selected bundle open, update it to reflect the status change:
            setSelectedBundleRowData(prev => {
                if (!prev) return prev;

                // Update the reservation status in the bundle
                const updatedReservations = prev.reservations.map(r => {
                    if (r.reservationId === reservationId) {
                        // Update the status based on action type
                        const newStatus = actionType === 'checkOut' ? 'CHECKED_OUT' : 'CHECKED_IN';
                        return { ...r, status: newStatus };
                    }
                    return r;
                });

                // Return the updated bundle
                return {
                    ...prev,
                    reservations: updatedReservations
                };
            });

            // Also update the master list to keep everything in sync
            if (dialogType === "checkOut") {
                setCheckOut(prevBundles =>
                    prevBundles.map(bundle => {
                        if (bundle.bundleId !== selectedBundleRowData?.bundleId) return bundle;
                        const updated = bundle.reservations.map(r => {
                            if (r.reservationId === reservationId) {
                                const newStatus = actionType === 'checkOut' ? 'CHECKED_OUT' : 'CHECKED_IN';
                                return { ...r, status: newStatus };
                            }
                            return r;
                        });
                        return { ...bundle, reservations: updated };
                    })
                );
            } else if (dialogType === "checkIn") {
                setCheckIn(prevBundles =>
                    prevBundles.map(bundle => {
                        if (bundle.bundleId !== selectedBundleRowData?.bundleId) return bundle;
                        const updated = bundle.reservations.map(r => {
                            if (r.reservationId === reservationId) {
                                const newStatus = actionType === 'checkOut' ? 'CHECKED_OUT' : 'CHECKED_IN';
                                return { ...r, status: newStatus };
                            }
                            return r;
                        });
                        return { ...bundle, reservations: updated };
                    })
                );
            }

        } catch (error) {
            console.error(`${actionType} failed:`, error);
            alert(`Failed to ${actionType}. Please try again.`);
        }
    };

    // Handles deletion confirmation and action
    const onDelClick = (event, reservationId) => {
        event.stopPropagation(); // Prevent row click event
        if (window.confirm("Are you sure you want to cancel this reservation?")) {
            handleCancelReservation(reservationId);
        }
    };

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    const handleBundleDialogTableRowClick = (params) => {
        setSelectedRowData(params.row);
        setItemDetailsDialogOpen(true);
    };

    const itemDetailsDialogClose = () => {
        setItemDetailsDialogOpen(false);
    };

    const handleAddDialogOpen = () => {
        setCreateDialogOpen(true); // Opens the Create Reservation dialog
    };

    const handleCreateDialogClose = () => {
        setCreateDialogOpen(false); // Closes the Create Reservation dialog
    };

    const handleOnSaveReservation = async (reservationData) => {
        handleCreateDialogClose();
        await fetchReservationsData();
    }

    // Checkout and checkin selection handlers for checklist
    // Removed as per edit hint

    // Bulk action handlers
    // Removed as per edit hint

    // datagrid column definitions - moved after function definitions
    const checkOutCols = checkOutColumns();
    const checkOutDetailsCols = getDetailedCheckOutColumns(handleReservationCall, handleCancelWrapper, userRole);

    const checkInCols = checkInColumns();
    const checkInDetailsCols = getDetailedCheckInColumns(handleReservationCall, handleCancelWrapper, userRole, fetchReservationsData, updateReservation);
    
    // Calculate dynamic table height for desktop/laptop, accounting for components above
    const { width, height } = useWindowSize();
    const isSmallScreen = width < 900;
    // Header: ~85px, ReservationHeader: ~100px, Search containers: ~80px, Padding: ~50px
    // Reduced subtraction to make tables extend down and push footer below viewport
    const tableHeight = !isSmallScreen 
        ? Math.max(400, height - 265) // Minimum 400px, subtract only components above table
        : 580; // Keep fixed height for mobile
    
    if (loading) {
        return (
            <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress />
            </div>
        );
    }
    return (
        <div>
            <ReservationHeader 
                onAddReservation={handleAddDialogOpen}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
            />
            <div className="table-container-reservations">
                <div className="table">
                    <ReservationsTable
                        title="Schedule for Check Out"
                        searchValue={checkOutSearch}
                        onSearchChange={(e) => setCheckOutSearch(e.target.value)}
                        searchPlaceholder="Search check-outs"
                        rows={checkOut.filter((row) =>
                        Object.values(row).some((value) =>
                            value?.toString().toLowerCase().includes(checkOutSearch.toLowerCase())
                        )
                        )}
                        columns={checkOutCols}
                        getRowId={(row) => `${row.userId}-${row.checkOut ? row.checkOut.toString() : ''}-${row.checkOutTime ? row.checkOutTime.toString() : ''}`}
                        onRowClick={(params) => handleMainTableRowClick(params, 'checkOut')}
                        rowHeight={80}
                        height={tableHeight}
                    />
                </div>

                <div className="table">
                    <ReservationsTable
                        title="Schedule for Check In"
                        searchValue={checkInSearch}
                        onSearchChange={(e) => setCheckInSearch(e.target.value)}
                        searchPlaceholder="Search check-ins"
                        rows={checkIn.filter((row) =>
                        Object.values(row).some((value) =>
                            value?.toString().toLowerCase().includes(checkInSearch.toLowerCase())
                        )
                        )}
                        columns={checkInCols}
                        getRowId={(row) => `${row.userId}-${row.checkIn ? row.checkIn.toString() : ''}-${row.checkInTime ? row.checkInTime.toString() : ''}`}
                        onRowClick={(params) => handleMainTableRowClick(params, 'checkIn')}
                        rowHeight={80}
                        height={tableHeight}
                    />
                </div>
            </div>

            <ReservationBundleDialog
                open={reservationBundleDialogOpen}
                onClose={reservationBundleDialogClose}
                rowData={selectedBundleRowData} 
                columns={dialogType === "checkOut" ? checkOutDetailsCols : checkInDetailsCols}
                onRowClick={(params) => handleBundleDialogTableRowClick(params)}
                dialogType={dialogType}
            />

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
            />

            <ItemDetailsDialog
                open={itemDetailsDialogOpen}
                onClose={itemDetailsDialogClose}
                dialogType={dialogType}
                selectedRowData={selectedRowData}
                userRole={userRole}
                handleReservationCall={(data, id) => handleReservationCall(data, id, dialogType)}
            />

            <CreateReservationDialog 
                open={createDialogOpen} 
                onClose={handleCreateDialogClose} 
                onSave={handleOnSaveReservation} 
                useDropDown={true}
            />
        </div>
    );
}

export default Reservations;
